import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ResourceFolder } from 'src/graphql/generated/graphql';
import { useResourceFolders } from 'src/hooks/query/resources/use-folders';
import { Routes } from '../../router/constants';
import { objectToQueryString } from 'src/utils/query-string';
import { Box, Typography } from '@mui/material';
import { Directory, RightArrow } from '@otp/icons';

type FoldersListProps = {
  parentId: string | null;
  withTotalCount?: boolean;
};

export const FoldersList = ({
  parentId,
  withTotalCount = false,
}: FoldersListProps) => {
  const [expandedFolders, setExpandedFolders] = useState<string[]>([]);

  const { t } = useTranslation(undefined, {
    keyPrefix: 'resources.file-explorer',
  });

  const navigate = useNavigate();

  const { folders, loading } = useResourceFolders({
    filter: {
      parentId,
    },
    resourcesCountFilter: {
      isDisabled: false,
    },
  });

  const totalCount = folders.reduce(
    (acc, folder) => acc + folder.resourcesCount,
    0,
  );

  const onExpandFolder = (folderId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedFolders([...expandedFolders, folderId]);
  };

  const onCollapseFolder = (folderId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedFolders(expandedFolders.filter((id) => id !== folderId));
  };

  const isFolderExpanded = (folderId: string) => {
    return expandedFolders.includes(folderId);
  };

  const openFolder = (folder: ResourceFolder, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(
      `${Routes.resources.path}?${objectToQueryString({
        filter: {
          folderIds: [folder.id],
        },
      })}`,
    );
  };

  if (folders.length === 0 && !loading) {
    return (
      <Typography variant="subtitle2" className="my-2">
        {t('no-folders')}
      </Typography>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-2 my-4 ">
        {folders
          .filter(({ resourcesCount }) => resourcesCount > 0)
          .map((folder) => (
            <div key={folder.id}>
              <div className="flex flex-row gap-3 items-center pl-2">
                <div>
                  <Directory />
                </div>
                <div
                  className="flex flex-col gap-0 w-full hover:underline "
                  onClick={(event) => openFolder(folder, event)}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    className="flex gap-2 items-center"
                  >
                    {folder.name}
                  </Typography>
                  <Typography variant="subtitle2">
                    {folder.resourcesCount} {t('resources')}
                  </Typography>
                </div>
                <div>
                  {isFolderExpanded(folder.id) ? (
                    <Box
                      className="p-2 hover:bg-gray-100 rounded-full"
                      onClick={(event) => onCollapseFolder(folder.id, event)}
                    >
                      <RightArrow className="rotate-90" />
                    </Box>
                  ) : (
                    <Box
                      className="p-2 hover:bg-gray-100 rounded-full"
                      onClick={(event) => onExpandFolder(folder.id, event)}
                    >
                      <RightArrow />
                    </Box>
                  )}
                </div>
              </div>

              {isFolderExpanded(folder.id) && (
                <div className="pl-4">
                  <FoldersList parentId={folder.id} />
                </div>
              )}
            </div>
          ))}
      </div>
      {withTotalCount && (
        <div className="flex flex-row gap-3 items-center">
          {t('resources-overall', {
            count: totalCount,
          })}
        </div>
      )}
    </div>
  );
};
